import { useStores } from '@/stores/RootStoreContext';
import { jwtDecode } from "jwt-decode";
import {
  BookDashed,
  BookOpen,
  Brain,
  Building2,
  ChevronRight,
  CreditCard,
  Home,
  KeyRound,
  LogOut,
  MessageSquareText,
  Moon,
  Sun,
  User,
  User2,
  Users,
  Webhook,
  Workflow,
} from "lucide-react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LogoImg from '../../assets/logo-mini.svg';
import { AdminRole, EditorRole, OwnerRole } from "../../constants";
import OpenReplayContext from "../../context/openreplay";
import ThemeContext from "../../context/theme";
import UserContext from "../../context/users";
import { logout, refresh } from "../../services/auth";
import Blocked from '../blocked';
import Loading from "../loading";
import { cn } from '../../lib/cn';

function useNavItems(role) {
  return useMemo(() => {
    const items = [];

    if ([AdminRole, EditorRole, OwnerRole].includes(role)) {
      items.push({ key: '/', label: 'Painel', icon: Home });
      items.push({ key: '/assistants', label: 'Agentes', icon: Brain });
      items.push({ key: '/chatbots', label: 'Fluxo AI', icon: Workflow });
      items.push({ key: '/templates', label: 'Templates', icon: BookDashed });
    }

    if ([AdminRole, OwnerRole].includes(role)) {
      items.push({ key: '/client-platforms', label: 'Clientes', icon: Users });
      items.push({ key: '/plans/native-client-platform', label: 'Planos', icon: CreditCard });
    }

    const adminItems = [];
    if (role === AdminRole) {
      adminItems.push({ key: '/api-keys', label: 'API Keys', icon: KeyRound });
      adminItems.push({ key: '/users', label: 'Usuários', icon: User });
      adminItems.push({ key: '/companies', label: 'Empresas', icon: Building2 });
      adminItems.push({ key: '/agent-requests', label: 'Solicitações', icon: MessageSquareText });
      adminItems.push({ key: '/plans/chatbot', label: 'Planos Fluxo', icon: CreditCard });
      adminItems.push({ key: '/plans/assistant', label: 'Planos Gen.', icon: Brain });
      adminItems.push({ key: '/evolution', label: 'Evolution', icon: Webhook });
    }

    return { items, adminItems };
  }, [role]);
}

export default function AdminContainer({ children }) {
  const location = useLocation();
  const tracker = useContext(OpenReplayContext);
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [isFail, setIsFail] = useState(false);
  const [actualTheme, setActualTheme] = useContext(ThemeContext);
  const [companyBlocked, setCompanyBlocked] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(
    () => localStorage.getItem('sidebarExpanded') === 'true'
  );

  const { userStore } = useStores();
  const locationSplit = location?.pathname?.split('/');

  useEffect(() => {
    doRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doRefresh = useCallback(() => {
    setIsFail(false);
    const token = localStorage.getItem('token');
    let userRaw = localStorage.getItem('user');
    let parsedUser;
    try {
      parsedUser = JSON.parse(userRaw);
    } catch {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate('/login?redirect=' + location?.pathname);
    }
    if (!token || !parsedUser) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate('/login?redirect=' + location?.pathname);
      return;
    }
    const decoded = jwtDecode(token);
    const expMs = (decoded?.exp ?? 0) * 1000;
    const msLeft = expMs - Date.now();
    const twoDaysMs = 2 * 24 * 60 * 60 * 1000;
    if (!Number.isFinite(expMs) || msLeft > twoDaysMs) {
      setUser(parsedUser);
      setCompanyBlocked(parsedUser?.company?.isBlocked);
      tracker.setUserID(parsedUser?.email);
      setIsFail(false);
      return;
    }
    refresh()
      .then(({ data }) => {
        localStorage.setItem("token", data?.token);
        localStorage.setItem("user", JSON.stringify(data?.user));
        setUser(data?.user);
        setCompanyBlocked(data?.user?.company?.isBlocked);
        tracker.setUserID(data?.user?.email);
        setIsFail(false);
      })
      .catch((e) => {
        if (e.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate('/login?redirect=' + location?.pathname);
        } else setIsFail(true);
        console.error(e);
      });
  }, [location?.pathname, navigate, tracker]);

  useEffect(() => {
    userStore.setUser(user);
  }, [user, userStore]);

  const selectedKeys = useMemo(
    () => [location?.pathname, "/" + locationSplit[1], "/" + locationSplit.slice(1, 3).join("/")],
    [location?.pathname, locationSplit]
  );

  const toggleSidebar = useCallback(() => {
    setSidebarExpanded((v) => {
      localStorage.setItem('sidebarExpanded', String(!v));
      return !v;
    });
  }, []);

  if (companyBlocked) {
    return (
      <UserContext.Provider value={[user, setUser]}>
        <Blocked />
      </UserContext.Provider>
    );
  }

  if (Object.keys(user)?.length <= 0) {
    return (
      <UserContext.Provider value={[user, setUser]}>
        <Loading isFail={isFail} tryAgain={doRefresh} title={'Autenticando...'} />
      </UserContext.Provider>
    );
  }

  return (
    <UserContext.Provider value={[user, setUser]}>
      <div
        className={cn(
          'flex h-screen w-full overflow-hidden bg-[var(--dash-bg)]',
          sidebarExpanded ? '[--sidebar-width:200px]' : '[--sidebar-width:56px]'
        )}
      >
        <Sidebar
          user={user}
          selectedKeys={selectedKeys}
          expanded={sidebarExpanded}
          onToggle={toggleSidebar}
          onNavigate={(key) => !user?.company?.isBlocked && navigate(key)}
          onThemeToggle={() => setActualTheme((old) => (old === 'dark' ? 'light' : 'dark'))}
          isDark={actualTheme === 'dark'}
          onLogout={() => logout(navigate)}
        />
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {children}
        </main>
        <BottomBar
          user={user}
          selectedKeys={selectedKeys}
          onNavigate={(key) => !user?.company?.isBlocked && navigate(key)}
        />
      </div>
    </UserContext.Provider>
  );
}

function Sidebar({ user, selectedKeys, expanded, onToggle, onNavigate, onThemeToggle, isDark, onLogout }) {
  const { items, adminItems } = useNavItems(user?.role);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  function isActive(key) {
    return selectedKeys.some((sk) => sk === key);
  }

  return (
    <nav
      className={cn(
        'hidden shrink-0 flex-col overflow-hidden border-r border-white/[0.06] bg-[#0e0a16] transition-[width,min-width] duration-[250ms] ease-out md:flex',
        expanded ? 'w-[200px] min-w-[200px]' : 'w-14 min-w-14'
      )}
    >
      <div className={cn('flex shrink-0 items-center py-5', expanded ? 'gap-3 px-4' : 'justify-center')}>
        <Link to="/" className="shrink-0">
          <img src={LogoImg} alt="Verboo" width={22} className="transition-transform duration-200 hover:scale-110" />
        </Link>
        {expanded && <span className="truncate text-sm font-semibold text-white">Verboo</span>}
      </div>

      <div className="flex flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden px-2 py-2">
        {items.map((item) => (
          <NavItem key={item.key} item={item} active={isActive(item.key)} expanded={expanded} onClick={() => onNavigate(item.key)} />
        ))}

        {adminItems.length > 0 && (
          <>
            <div className={cn('mx-auto my-2 h-px bg-white/[0.08]', expanded ? 'w-full' : 'w-6')} />
            {adminItems.map((item) => (
              <NavItem key={item.key} item={item} active={isActive(item.key)} expanded={expanded} onClick={() => onNavigate(item.key)} />
            ))}
          </>
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-2 px-2 pb-4 pt-2">
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            'flex h-9 items-center gap-2.5 rounded-xl text-white/40 transition-all duration-200 hover:bg-white/[0.08]',
            expanded ? 'px-3' : 'w-9 justify-center'
          )}
          aria-label={expanded ? 'Colapsar menu' : 'Expandir menu'}
        >
          <ChevronRight
            size={14}
            strokeWidth={2}
            className={cn('shrink-0 transition-transform duration-300', expanded && 'rotate-180')}
          />
          {expanded && <span className="truncate text-[11px] font-medium">Colapsar</span>}
        </button>

        <button
          type="button"
          onClick={onThemeToggle}
          className={cn(
            'flex h-9 items-center gap-2.5 rounded-xl text-white/50 transition-all duration-200 hover:bg-white/[0.08]',
            expanded ? 'px-3' : 'w-9 justify-center'
          )}
          aria-label={isDark ? 'Modo claro' : 'Modo escuro'}
        >
          {isDark
            ? <Moon size={15} strokeWidth={2} className="shrink-0" />
            : <Sun size={15} strokeWidth={2} className="shrink-0" />}
          {expanded && (
            <span className="truncate text-[11px] font-medium">
              {isDark ? 'Modo escuro' : 'Modo claro'}
            </span>
          )}
        </button>

        <div className="group relative">
          <a
            href="https://verboo.ai/pt/docs"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'flex h-9 items-center gap-2.5 rounded-xl text-white/40 transition-all duration-200 hover:bg-white/[0.08]',
              expanded ? 'px-3' : 'w-9 justify-center'
            )}
            aria-label="Documentação"
          >
            <BookOpen size={15} strokeWidth={2} className="shrink-0" />
            {expanded && <span className="truncate text-[11px] font-medium">Documentação</span>}
          </a>
          {!expanded && (
            <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-[var(--dash-text)] px-2.5 py-1.5 text-[11px] font-medium text-[var(--dash-bg)] opacity-0 shadow-[0_4px_16px_rgba(0,0,0,0.35)] transition-opacity duration-150 group-hover:opacity-100">
              Documentação
            </div>
          )}
        </div>

        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen((v) => !v)}
            className={cn(
              'flex h-10 items-center gap-2.5 rounded-xl transition-all duration-200 hover:bg-white/[0.06]',
              expanded ? 'w-full px-2' : 'w-10 justify-center'
            )}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#ad34fe] to-[#7b1fd4] text-[10px] font-bold uppercase tracking-wider text-white shadow-[0_0_14px_rgba(173,52,254,0.3)]">
              {user?.name?.substring(0, 2)}
            </div>
            {expanded && (
              <div className="flex min-w-0 flex-col text-left">
                <span className="truncate text-xs font-medium text-white">{user?.name}</span>
                <span className="truncate text-[10px] text-white/35">{user?.email}</span>
              </div>
            )}
          </button>

          <UserPopupMenu
            open={userMenuOpen}
            expanded={expanded}
            anchorRef={userMenuRef}
            user={user}
            userRole={user?.role}
            onClose={() => setUserMenuOpen(false)}
            onLogout={() => { setUserMenuOpen(false); onLogout(); }}
          />
        </div>
      </div>
    </nav>
  );
}

function NavItem({ item, active, onClick, expanded }) {
  const Icon = item.icon;
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'relative flex h-10 items-center gap-2.5 rounded-xl transition-all duration-200',
          expanded ? 'w-full px-3' : 'w-10 justify-center',
          active ? 'bg-white/[0.1] text-white' : 'text-white/45 hover:bg-white/[0.06]'
        )}
      >
        {active && (
          <div className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-[#ad34fe] shadow-[0_0_10px_rgba(173,52,254,0.6)]" />
        )}
        <Icon size={17} strokeWidth={active ? 2.2 : 1.8} className="shrink-0" />
        {expanded && <span className="truncate text-xs font-medium">{item.label}</span>}
      </button>
      {!expanded && (
        <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-[var(--dash-text)] px-2.5 py-1.5 text-[11px] font-medium text-[var(--dash-bg)] opacity-0 shadow-[0_4px_16px_rgba(0,0,0,0.35)] transition-opacity duration-150 group-hover:opacity-100">
          {item.label}
        </div>
      )}
    </div>
  );
}

function UserPopupMenu({ open, expanded, anchorRef, user, userRole, onClose, onLogout }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open || expanded || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: rect.top - 4,
      left: rect.right + 8,
    });
  }, [open, expanded, anchorRef]);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e) {
      if (!anchorRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        onClose();
      }
    }
    function onEsc(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, anchorRef, onClose]);

  if (!open) return null;

  const menuContent = (
    <div
      ref={menuRef}
      className="animate-nav-popup min-w-[180px] overflow-hidden rounded-xl border border-[var(--dash-border-strong)] bg-[var(--dash-surface)] py-1 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.45)]"
      style={!expanded ? { position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, transform: 'translateY(-100%)' } : undefined}
    >
      <Link to={`/profile/${user?.id}`} onClick={onClose}>
        <UserMenuItem icon={User2} label="Perfil" />
      </Link>
      {[AdminRole, OwnerRole].includes(userRole) && (
        <Link to="/my-company" onClick={onClose}>
          <UserMenuItem icon={Building2} label="Minha Empresa" />
        </Link>
      )}
      <div className="my-1 h-px bg-[var(--dash-border)]" />
      <button type="button" onClick={onLogout} className="w-full">
        <UserMenuItem icon={LogOut} label="Sair" danger />
      </button>
    </div>
  );

  if (!expanded) {
    return createPortal(menuContent, document.body);
  }

  return (
    <div className="absolute bottom-full left-0 z-50 mb-2">
      {menuContent}
    </div>
  );
}

function UserMenuItem({ icon: Icon, label, danger }) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-colors hover:bg-[var(--dash-surface-hover)]',
        danger ? 'text-[#d61f3f]' : 'text-[var(--dash-text)]'
      )}
    >
      <Icon size={14} strokeWidth={2} />
      {label}
    </div>
  );
}

function BottomBar({ user, selectedKeys, onNavigate }) {
  const { items } = useNavItems(user?.role);
  const assistantItem = items.find((i) => i.key === '/assistants');
  const otherItems = items.filter((i) => i.key !== '/assistants').slice(0, 4);
  const left = otherItems.slice(0, 2);
  const right = otherItems.slice(2, 4);

  function isActive(key) {
    return selectedKeys.some((sk) => sk === key);
  }

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 flex h-[68px] items-end justify-around border-t border-white/[0.06] bg-[#0e0a16] px-2 pb-[env(safe-area-inset-bottom)] md:hidden">
      {left.map((item) => (
        <BottomBarItem key={item.key} item={item} active={isActive(item.key)} onClick={() => onNavigate(item.key)} />
      ))}
      {assistantItem && (
        <BottomBarItem item={assistantItem} active={isActive(assistantItem.key)} onClick={() => onNavigate(assistantItem.key)} highlight />
      )}
      {right.map((item) => (
        <BottomBarItem key={item.key} item={item} active={isActive(item.key)} onClick={() => onNavigate(item.key)} />
      ))}
    </nav>
  );
}

function BottomBarItem({ item, active, onClick, highlight }) {
  const Icon = item.icon;
  return (
    <button type="button" onClick={onClick} className="flex flex-col items-center gap-1 py-2">
      <div
        className={cn(
          'flex items-center justify-center rounded-xl transition-all duration-200',
          highlight ? 'h-10 w-10' : 'h-8 w-8',
          active && !highlight && 'bg-white/[0.12]',
          highlight && active && 'bg-gradient-to-br from-[#ad34fe] to-[#7b1fd4] shadow-[0_0_16px_rgba(173,52,254,0.4)] text-white border border-[rgba(173,52,254,0.5)]',
          highlight && !active && 'bg-[rgba(173,52,254,0.15)] border border-[rgba(173,52,254,0.25)] text-white',
          !highlight && active && 'text-[#ad34fe]',
          !highlight && !active && 'text-white/40'
        )}
      >
        <Icon size={highlight ? 20 : 18} strokeWidth={active ? 2.2 : 1.8} />
      </div>
      <span
        className={cn(
          'text-[9px]',
          highlight ? 'font-semibold' : 'font-medium',
          active && highlight && 'text-[#ad34fe]',
          active && !highlight && 'text-white',
          !active && 'text-white/35'
        )}
      >
        {item.label}
      </span>
    </button>
  );
}
